"use client";

import { usePointerInteractionMedia } from "@/components/feature/pointer-interaction-media";
import { usePreviewIntentStore } from "@/components/feature/preview-intent";
import { useIsKeyboardModality } from "@/hooks/useInputModality";
import { useCallback, useEffect, useMemo, useRef } from "react";

/**
 * Default intent debounce for preview activation.
 * Slightly longer than a typical hover delay to reduce accidental activation
 * (especially for keyboard focus traversal and motor-impaired users).
 */
export const DEFAULT_DEBOUNCE_MS = 180;

export interface PreviewIntentHandlers {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onFocus: () => void;
  onBlur: () => void;
}
interface UsePreviewIntentHandlersParams {
  previewId: string;
  enabled: boolean;
  debounceMs?: number;
}

/**
 * Returns event handlers that activate a preview based on user intent.
 * - Hover activates after a small debounce.
 * - Focus activates only when the last input modality was keyboard.
 */
export function usePreviewIntentHandlers({
  previewId,
  enabled,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}: UsePreviewIntentHandlersParams): PreviewIntentHandlers {
  const store = usePreviewIntentStore();
  const isKeyboardModality = useIsKeyboardModality();
  const { canHover, hasPointerFine, prefersReducedMotion } = usePointerInteractionMedia();
  const timerRef = useRef<number | null>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const activate = useCallback(() => {
    store.setActiveId(previewId);
  }, [store, previewId]);

  const deactivateIfActive = useCallback(() => {
    if (store.getActiveId() === previewId) {
      store.setActiveId(null);
    }
  }, [store, previewId]);

  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, []);

  const onMouseEnter = useCallback(() => {
    if (!enabled) return;
    if (!canHover || !hasPointerFine) return;
    if (prefersReducedMotion) return;
    clearTimer();
    timerRef.current = window.setTimeout(activate, debounceMs);
  }, [enabled, canHover, hasPointerFine, prefersReducedMotion, activate, debounceMs]);

  const onMouseLeave = useCallback(() => {
    clearTimer();
    deactivateIfActive();
  }, [deactivateIfActive]);

  const onFocus = useCallback(() => {
    if (!enabled) return;
    if (!isKeyboardModality) return;
    if (prefersReducedMotion) return;
    clearTimer();
    timerRef.current = window.setTimeout(activate, debounceMs);
  }, [enabled, isKeyboardModality, prefersReducedMotion, activate, debounceMs]);

  const onBlur = useCallback(() => {
    clearTimer();
    deactivateIfActive();
  }, [deactivateIfActive]);

  return useMemo(
    () => ({
      onMouseEnter,
      onMouseLeave,
      onFocus,
      onBlur,
    }),
    [onMouseEnter, onMouseLeave, onFocus, onBlur]
  );
}
